import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { directory, isForbidden, programs, schedule, services, sites } from '@cwos/api-client';
import type { schedule as SCH } from '@cwos/api-client';
import { Origin, Pill, Refused, ScopeNote, SkeletonSet, SuccessEmpty } from '@cwos/ui-admin';
import { t } from '@cwos/ui-shared';
import { mayListWorkers, mayReadIntegrationsHealth, usePermissions } from '../permissions.js';
import { OWN_CASELOAD } from '../delivery.js';
import { useConnectorLookup } from '../visits/provenance.js';
import { clientNameResolver } from '../visits/visitLogic.js';
import { authorityGate, gateOpen } from '../authorityGate.js';
import { GateNotice } from '../GateNotice.js';
import { useNav } from '../navigation.js';
import { listPhase, nameResolver } from '../listState.js';
import { pickerState, type PickerState } from '../pickerState.js';
import { SettingsCard, SettingsField, SettingsHeader } from '../settings/common.js';
import { formatClock, formatDay } from '../time/timeLogic.js';
import { formatClockTime } from '../../lib/timeFormat.js';
import { localDateIn } from './coverageLogic.js';
import { useOrgCalendar } from './orgCalendar.js';
import {
  emptyShiftDraft,
  shiftCreatePayload,
  shiftDraftFromShift,
  shiftErrorMessage,
  shiftUpdatePayload,
  toDatetimeLocalInput,
  validateShiftDraft,
  coverageGaps,
  type ShiftDraft,
} from './scheduleLogic.js';
import {
  DAY_AXIS,
  boardDays,
  boardRows,
  boardWindow,
  calendarStateLabel,
  dayKey,
  daySpan,
  filterItems,
  tally,
  tallyByDay,
  toCalendarItems,
  type BoardView,
  type CalendarItem,
  type CalendarState,
} from './scheduleBoard.js';
import { ShiftDrawer } from './ShiftDrawer.js';
import { VisitDrawer } from './VisitDrawer.js';
import { AttributionPicker } from './AttributionPicker.js';
import { LoadFailure } from '../LoadFailure.js';

/**
 * Scheduling — the plan, and whether reality matched it.
 *
 * The shift API has existed since Task 11. Reads (coverage, all shifts, the
 * worker week) came first; create/assign/edit/cancel — the founder's P0-3
 * acceptance criterion for an administrator — did not, until now. Creating,
 * editing and cancelling all run through the same `schedule:write` /
 * `schedule:manage` split the API already enforces; the screen just adds the
 * forms.
 *
 * Coverage ("started, no session") only means anything if a worked session
 * actually gets linked to the shift it fulfilled — the API supports that link
 * but nothing called it automatically. This screen now runs that match (for a
 * `schedule:manage` viewer) whenever it loads a worker's week, so "no session"
 * reflects a real gap rather than a link nobody ever asked the server to make.
 */

/**
 * The presentations of the same reads (Phase C — owner directive: the calendar
 * becomes an actual working scheduler; P0 — Schedule is the organization's
 * calendar). `week` (the default), `day` and `month` are the canvas — workers ×
 * days/time over shifts AND client visits; `coverage` and `all` are the
 * preserved shift list surfaces (the founder's parity rule: consolidation never
 * removes the underlying view, and the cancel/edit flows live in `all`).
 */
type View = BoardView | 'coverage' | 'all';

/** The order states are told in, on the stats line, the legend and the month cells. */
const STATE_ORDER: CalendarState[] = [
  'upcoming',
  'live',
  'covered',
  'gap',
  'delivered',
  'not_started',
  'no_show',
  'cancelled',
  'unlisted',
  'unclassified',
];

const DAY_MS = 86_400_000;

export function Scheduling() {
  const navigate = useNav();
  const qc = useQueryClient();
  const permissions = usePermissions();
  const { has } = permissions;
  const [view, setView] = useState<View>('week');
  const [days, setDays] = useState(7);
  /** The canvas's anchor day; ‹ › move by a day, a week or a month per the view. */
  const [anchor, setAnchor] = useState(() => new Date());
  const [drawerId, setDrawerId] = useState<string | null>(null);
  const [visitDrawerId, setVisitDrawerId] = useState<string | null>(null);
  const [programFilter, setProgramFilter] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  /** `?worker=<id>` opens the calendar on one person (the worker profile's door). */
  const search = useSearch({ strict: false }) as { worker?: unknown };
  const [workerFilter, setWorkerFilter] = useState(() =>
    typeof search.worker === 'string' ? search.worker : '',
  );
  const [clientFilter, setClientFilter] = useState('');
  const [originFilter, setOriginFilter] = useState<'' | 'ehr' | 'phibian'>('');
  const [stateFilter, setStateFilter] = useState<'' | CalendarState | 'unresolved'>('');
  /** Include roster workers with no shifts in the window (canvas planning aid). */
  const [showEveryone, setShowEveryone] = useState(false);
  const isBoard = view === 'week' || view === 'day' || view === 'month';
  /**
   * The ORGANIZATION's calendar: its week start lays out the Week and Month
   * grids and its zone decides which day a visit falls on, so a week here is
   * the week the dashboard counted (Generation Hope: Sunday, America/Chicago).
   */
  const calendar = useOrgCalendar();
  const days_ = isBoard ? boardDays(anchor, view, calendar.weekStartsOn) : null;

  const canWrite = has('schedule:write');
  const canManage = has('schedule:manage');
  /**
   * The roster, asked the way the roster route decides it. `GET
   * /directory/workers` charges `can()` at organization scope twice —
   * `guard(..., 'directory:read')` and then `mayReadWorkerIdentity` for
   * `user:read` — and 403s rather than returning fewer people, so a
   * program-scoped holder must be told the picker is withheld, not shown an
   * empty one.
   *
   * Unheld and NOT YET KNOWN are different facts and only the first is a
   * sentence about this reader's account: `permissions` resolves to an empty
   * set while it loads and after it fails, which is right for not firing the
   * query and wrong for saying the roster is withheld. The refusal is claimed
   * only from a resolved set; until then the picker says it is still loading,
   * which is what it is.
   */
  const canListWorkers = mayListWorkers(permissions);
  const rosterWithheld = !permissions.isLoading && !permissions.isError && !canListWorkers;

  const [creating, setCreating] = useState(false);
  const [createDraft, setCreateDraft] = useState<ShiftDraft>(() => emptyShiftDraft(new Date()));
  const [createError, setCreateError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<ShiftDraft>(() => emptyShiftDraft(new Date()));
  const [editError, setEditError] = useState<string | null>(null);

  const [cancelingId, setCancelingId] = useState<string | null>(null);
  /**
   * Which shift's cancel failed, and what the server said. Keyed by id rather
   * than a bare string so the message can only ever appear against the row it
   * belongs to — a schedule shows many shifts and a floating "that didn't work"
   * names none of them.
   */
  const [cancelError, setCancelError] = useState<{ id: string; message: string } | null>(null);

  /**
   * WHO THIS CANCELLATION IS ATTRIBUTABLE TO — and there is no default.
   *
   * The empty string is not a sixth attribution; it is "nobody has chosen yet",
   * and `Confirm cancel` stays disabled while it holds. Defaulting the picker
   * would make the commonest path record whichever member happened to be first
   * in the list, and every cancellation in the organization would then carry the
   * same attribution whether or not anybody meant it — a column full of a value
   * nobody chose is worse than a column full of nulls, because it looks answered.
   *
   * `'unknown'` is offered explicitly for a scheduler who genuinely does not
   * know. That is a different fact from not having been asked, and the
   * vocabulary already distinguishes them.
   */
  const [cancelAttribution, setCancelAttribution] = useState<SCH.Attribution | ''>('');
  /**
   * What the last successful cancellation DID, in the server's own terms.
   *
   * `reasonTaskId` is non-null exactly when the staff member was asked, and a
   * scheduler who has just attributed a cancellation to a worker needs to know
   * that a question has gone to a person. Rendering nothing would leave the ask
   * — the single most consequential side effect of this door — invisible at the
   * moment it happens.
   */
  const [cancelOutcome, setCancelOutcome] = useState<{
    id: string;
    asked: boolean;
    rescheduleTracked: boolean;
  } | null>(null);

  const window = useMemo(() => {
    if (days_) return boardWindow(days_, calendar.timeZone);
    const now = new Date();
    const from = new Date(now.getTime() - 1 * DAY_MS);
    const to = new Date(now.getTime() + days * DAY_MS);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [days, days_, calendar.timeZone]);

  /**
   * ONE read for the whole screen, coverage included.
   *
   * This used to be a plain shift list plus a per-worker fan-out: one week
   * read and one auto-match WRITE per worker who had a shift in the window,
   * because only the week view returned the shift↔session join. Measured on
   * 120 workers with 600 shifts: 240 connections and 2,886 database queries,
   * ~1.5s of database time, and 241 HTTP requests from the browser — for a
   * screen whose own list read is 6 queries and 7ms.
   *
   * `?includeSession=true` puts the join on the list route, and the sweep takes
   * no `workerId`, so both are constant in headcount. That is the rule the
   * workforce and credentials views already follow: a fixed number of queries
   * however many people there are, joined in memory.
   *
   * The sweep stays best-effort and stays FIRST: without it a session a worker
   * genuinely worked is never linked to the shift it fulfilled, and "no
   * session" would misreport real work as a gap forever.
   */
  const shifts = useQuery({
    queryKey: ['schedule', 'shifts', window.from, window.to, canManage],
    queryFn: async () => {
      if (canManage) {
        try {
          await schedule.autoMatch({ from: window.from, to: window.to });
        } catch {
          /* best-effort; the read below still reflects reality */
        }
      }
      // `coverageRoster`, NOT `shiftsWithCoverage`: the envelope whole. The
      // rows alone cannot say whose roster they are, and this screen makes a
      // safety claim over them.
      return schedule.coverageRoster(window);
    },
    enabled: has('schedule:read'),
  });
  const workers = useQuery({
    queryKey: ['directory', 'workers'],
    queryFn: () => directory.workers(),
    enabled: canListWorkers,
  });

  /**
   * THE OTHER HALF OF THE CALENDAR: client visits — for an EHR tenant, every
   * imported occurrence. `GET /services/scheduled` carries the canonical
   * `deliveredBucket` and the credited staff (`workerIds`) per row, so the board
   * colours a visit with the number every metric counts it in.
   *
   * A different key from the shifts (`service:read`) and its own `scoped` frame
   * (`schedule:manage` widens it). The server's window is closed at both ends,
   * so the upper bound is pulled in by a millisecond to read the board's
   * half-open [from, to) — a visit at next midnight belongs to no column.
   */
  const visitWindow = useMemo(
    () => ({ from: window.from, to: new Date(Date.parse(window.to) - 1).toISOString() }),
    [window],
  );
  const canReadVisits = has('service:read');
  const visits = useQuery({
    queryKey: ['services', 'scheduled', visitWindow.from, visitWindow.to],
    queryFn: () => services.scheduled(visitWindow),
    enabled: canReadVisits,
  });
  const clients = useQuery({
    queryKey: ['directory', 'clients'],
    queryFn: () => directory.clients(),
    enabled: canReadVisits && isBoard && has('directory:read'),
  });
  const directoryGate = authorityGate(permissions, 'directory:read').kind;
  const clientLabel = useMemo(
    () => clientNameResolver(directoryGate, clients),
    // `clients.data` / `clients.isError`, not the per-render wrapper (Visits.tsx).
    [clients.data, clients.isError, directoryGate],
  );
  const connectorLookup = useConnectorLookup(mayReadIntegrationsHealth(has) && canReadVisits);

  // Fetched for an open create/edit form AND for the canvas, which reads
  // these for its filters and the drawer's program/site names. Still gated on
  // the reads' own keys by the routes; a refusal leaves the filter absent.
  const formOpen = creating || editingId !== null;
  const programOptions = useQuery({
    queryKey: ['programs', 'options'],
    queryFn: () => programs.list({ status: 'active', limit: 200 }),
    enabled: (formOpen && canWrite) || isBoard,
  });
  const siteOptions = useQuery({
    queryKey: ['sites', 'options'],
    queryFn: () => sites.list({ limit: 200, sort: 'name' }),
    enabled: (formOpen && canWrite) || isBoard,
  });

  // The directory here is a secondary read with no error branch of its own; a
  // single 500 used to relabel every worker on the screen as somebody this
  // reader was not allowed to see.
  const nameOf = useMemo(() => nameResolver(workers, canListWorkers), [workers, canListWorkers]);

  /**
   * `worker.id` → `app_user.id`, for the one consumer that needs the OTHER key.
   *
   * `nameResolver` keys by `worker.id`, which is right for everything on this
   * screen except the cancellation record: `reason_given_by_user_id` is an
   * `app_user.id`, and `PROFILE-PHOTO-CONTRACT.md` is explicit that the two are
   * not interchangeable. `null` — the directory has not answered, the reader may
   * not read it, or this worker has no login — is passed through as null so the
   * drawer reports "we cannot tell whose account this is" rather than asserting
   * the worker answered for themselves.
   */
  const userIdOf = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const w of workers.data ?? []) map.set(w.id, w.userId ?? null);
    return (workerId: string): string | null => map.get(workerId) ?? null;
  }, [workers.data]);

  function invalidateSchedule(): void {
    void qc.invalidateQueries({ queryKey: ['schedule'] });
  }

  const createShift = useMutation({
    mutationFn: () => schedule.createShift(shiftCreatePayload(createDraft)),
    onSuccess: () => {
      setCreating(false);
      setCreateDraft(emptyShiftDraft(new Date()));
      setCreateError(null);
      invalidateSchedule();
    },
    onError: (err: unknown) => {
      setCreateError(shiftErrorMessage(err, 'We couldn’t create that shift.'));
    },
  });

  const updateShift = useMutation({
    mutationFn: (id: string) => schedule.updateShift(id, shiftUpdatePayload(editDraft)),
    onSuccess: () => {
      setEditingId(null);
      setEditError(null);
      invalidateSchedule();
    },
    onError: (err: unknown) => {
      setEditError(shiftErrorMessage(err, 'We couldn’t save those changes.'));
    },
  });

  /**
   * Calling a shift off, and SAYING SO WHEN IT DID NOT HAPPEN.
   *
   * `onError` used to do exactly what `onSuccess` does — close the confirm and
   * write nothing — so a failed cancel was rendered identically to one that
   * worked: the confirm row vanished, the shift kept its old status pill, and
   * the only signal the operator had was the affordance disappearing. That
   * disappearance IS the success signal on this row, and it was being fired on
   * failure. `Visits.tsx` names this screen in its own header as the
   * counter-example; this is that comment's fix.
   *
   * So the failure branch does the opposite of the success branch: the confirm
   * stays OPEN, because a control still on screen is what tells a reader the
   * thing they asked for has not been done, and the server's own sentence goes
   * next to it.
   *
   * ---------------------------------------------------------------------------
   * THE FULL DOOR, NOT THE PLAIN ONE
   * ---------------------------------------------------------------------------
   * This used to call `schedule.cancelShift` — `POST /schedule/shifts/:id/cancel`
   * — which sets the shift's status and records NOTHING ELSE. Five states no
   * surface could then produce or read: the attribution, the reason category,
   * the single ask, its deadline, and the escalation. A shift went from
   * "scheduled" to "cancelled" and the product could not afterwards say who
   * called it off, whose side it came from, or whether anybody had been asked
   * why.
   *
   * It now calls `POST /schedule/shifts/:id/cancellation`, which additionally
   * writes the `shift_cancellation` row, opens the rescheduling obligation for
   * the client, and — for a WORKER-attributed cancellation with no reason
   * already recorded — puts the single ask to the staff member with a deadline.
   *
   * The plain door is deliberately still in the client for a shift being unwound
   * rather than called off (a duplicate, a booking error). It is simply no
   * longer the only door a screen can open.
   */
  const cancelShift = useMutation({
    mutationFn: (v: { id: string; input: SCH.CancelWithAttributionInput }) =>
      schedule.cancelShiftWithAttribution(v.id, v.input),
    onMutate: () => {
      setCancelError(null);
      setCancelOutcome(null);
    },
    onSuccess: (result, v) => {
      setCancelingId(null);
      setCancelError(null);
      setCancelAttribution('');
      // `reasonTaskId !== null` is the SERVER's answer to "was anybody asked",
      // not this screen's guess from the attribution. A client cancellation
      // raises no question, and so does a worker cancellation that arrived with
      // its reason already recorded — two different paths to the same null, and
      // neither is derivable from the picker alone.
      setCancelOutcome({
        id: v.id,
        asked: result.reasonTaskId !== null,
        rescheduleTracked: result.rescheduleTaskId !== null,
      });
      invalidateSchedule();
    },
    onError: (err: unknown, v: { id: string; input: SCH.CancelWithAttributionInput }) => {
      // `cancelingId` deliberately NOT cleared: the confirm stays up.
      setCancelError({
        id: v.id,
        message: shiftErrorMessage(err, 'We couldn’t call that shift off.'),
      });
    },
  });

  /**
   * Coverage, from the list the screen already has.
   *
   * Removing the fan-out removed a state with it. When this was N queries,
   * PARTIAL failure was real and had to be reported — some weeks read, some
   * not, and a confident "every started shift has a session" over the gap. With
   * one query there is no partial: the read either answered or it did not, and
   * the screen's existing `shifts.isError` guard above already covers that.
   */
  const uncovered = useMemo(
    () => coverageGaps(shifts.data?.shifts ?? [], new Date()),
    [shifts.data],
  );

  // Unknown is not denied. `has()` is `false` while `/auth/me` is in flight and
  // `false` after it failed, and this branch used to render "you don't have
  // access to the schedule" off both — telling somebody who holds
  // `schedule:read` that they do not. `authorityGate` keeps the three apart and
  // only `refused` is entitled to the sentence.
  const readGate = authorityGate(permissions, 'schedule:read');
  if (!gateOpen(readGate)) {
    return (
      <GateNotice
        gate={readGate}
        what="read the schedule"
        heading="You don’t have access to the schedule"
        detail="Reading the schedule needs the schedule permission."
        onRetry={permissions.refetch}
      />
    );
  }

  // `GET /schedule/shifts` also 403s a non-`schedule:manage` caller with no
  // resolvable worker row — a refusal, arriving in the failure branch with a
  // retry button that cannot change it.
  if (shifts.isError && isForbidden(shifts.error)) {
    return (
      <Refused
        heading="The schedule is not shown to you"
        message="Your account has no worker record to read a schedule for, and reading everybody else's needs permission to manage the schedule. This is not an empty week."
      />
    );
  }
  if (shifts.isLoading || visits.isLoading) return <SkeletonSet surface="table" rows={6} />;
  if (shifts.isError && !shifts.data) {
    return (
      <LoadFailure
        error={shifts.error}
        message="We couldn’t load the schedule just now. Do not read this as an empty window."
        onRetry={() => void shifts.refetch()}
      />
    );
  }

  const rows = shifts.data?.shifts ?? [];

  /**
   * WHOSE SHIFTS THESE ARE.
   *
   * `GET /schedule/shifts` rewrites `filter.workerId` to the caller's own id
   * for anyone without `schedule:manage`. So "Shifts in window: 3" was one
   * worker's three under a card headed "Next 7 days", and — the one that
   * matters — "Every started shift has a session · Nothing planned has gone
   * unrecorded in this window" was a SAFETY CLAIM about the organization
   * derived from a one-worker list.
   *
   * THE SERVER NOW SAYS SO, and this reads the server rather than guessing.
   * `scope` exists on the roster envelope precisely because the narrowing was
   * invisible in the payload; re-deriving it from `has('schedule:manage')` was
   * the client answering a question about the SERVER's decision from its own
   * flattened permission set — which is wrong for a grant confined to one
   * program (`has` is true, `can` is false, the route narrows anyway) and is
   * exactly the re-derivation `ShiftRoster.scope` was added to retire.
   *
   * Before the roster answers there is no `scope` to read, and the permission
   * key is the best available guess — but nothing that consumes this renders
   * until `rows` exist, so the guess is never what a reader sees.
   */
  const ownShiftsOnly = shifts.data ? shifts.data.scope === 'own' : !canManage;

  /**
   * THE OTHER HALF OF THE SAME SENTENCE.
   *
   * `scope` says WHOSE shifts these are. `page.hasMore` says whether these are
   * ALL of them. Both are frames the rows cannot carry, and both falsify the
   * same claim: "Nothing planned has gone unrecorded in this window" is wrong on
   * a one-worker list read as the organization, and equally wrong on a first
   * page read as the whole window.
   *
   * `GET /schedule/shifts` was unbounded until now, so the question could not
   * arise — the list was always complete, however large, which is why it could
   * return all 12,159 shifts of the demo tenant in one response. Now that it is
   * bounded, a screen that renders a completeness claim has to read the bound.
   * Read from the server's own answer, never inferred from `rows.length`: a page
   * that happens to be exactly full is indistinguishable from a complete one.
   */
  const partialWindow = shifts.data?.page.hasMore ?? false;

  // --- The canvas — plain derivations over the rows in hand. ---------------
  const now = new Date();
  const cols = days_ ?? [];

  /**
   * WHAT THE VISIT HALF OF THE CALENDAR IS, said as one of four facts and never
   * as a zero: read (rows), withheld (the key is not held, or the route
   * refused), failed (a retry may help), or not yet known (the permission set
   * is still resolving). Only `read` contributes rows.
   */
  const serviceGate = authorityGate(permissions, 'service:read').kind;
  const visitsWithheld = serviceGate === 'refused' || (visits.isError && isForbidden(visits.error));
  const visitsFailed = visits.isError && !visits.data && !visitsWithheld;
  const visitRows = visits.data?.scheduledServices ?? [];
  const visitsRead = visits.data !== undefined;

  const items = toCalendarItems(rows, visitRows, now);
  const visitItems = items.filter((i) => i.kind === 'visit');
  /**
   * WHAT THE COUNTS COVER. The Month view READS and DRAWS the whole 6 × 7 grid
   * (the neighbouring months' days are context), but its headline and its
   * all-clear COUNT the anchor's month only — "October · N scheduled services"
   * must be October's N, the number analytics and the dashboard give for it.
   * Week and Day count what they draw.
   */
  const inPeriod = (iso: string): boolean =>
    view !== 'month' ||
    localDateIn(calendar.timeZone, new Date(iso)).slice(0, 7) === dayKey(anchor).slice(0, 7);
  /** A chip's clock, in the organization's zone like the column it sits in. */
  const clock = (iso: string): string => formatClockTime(iso, calendar.timeZone);
  const orgToday = localDateIn(calendar.timeZone, now);
  const periodVisits = visitItems.filter((i) => inPeriod(i.startsAt));
  const periodShifts = rows.filter((s) => inPeriod(s.startsAt));
  const periodUpcoming = periodShifts.filter((s) => Date.parse(s.startsAt) > now.getTime());
  const gaps = uncovered.filter((s) => inPeriod(s.startsAt));
  /**
   * Visits with no recorded outcome — what an all-clear must not talk over:
   * not started, no-show, a status Phibian does not recognise, and a
   * hand-booked visit still `scheduled` after its start (no inference either
   * way — it is simply not yet recorded).
   */
  const visitMisses = periodVisits.filter(
    (i) =>
      i.state === 'not_started' ||
      i.state === 'no_show' ||
      i.state === 'unclassified' ||
      (i.state === 'upcoming' && Date.parse(i.startsAt) <= now.getTime()),
  ).length;
  const windowTally = tally(periodVisits);
  // Owner ruling: scheduled services exclude cancelled and no-longer-listed visits.
  const scheduledServices =
    periodVisits.length - (windowTally.cancelled ?? 0) - (windowTally.unlisted ?? 0);
  const boardItems = isBoard
    ? filterItems(items, {
        workerId: workerFilter || undefined,
        clientId: clientFilter || undefined,
        programId: programFilter || undefined,
        siteId: siteFilter || undefined,
        origin: originFilter || undefined,
        state: stateFilter || undefined,
      })
    : [];
  const filtersActive = Boolean(
    workerFilter || clientFilter || programFilter || siteFilter || originFilter || stateFilter,
  );
  function clearFilters(): void {
    setWorkerFilter('');
    setClientFilter('');
    setProgramFilter('');
    setSiteFilter('');
    setOriginFilter('');
    setStateFilter('');
  }
  // Filter options come from the window's own items — never a fabricated
  // dimension. A program or site picker appears only when something in the
  // window carries one (Generation Hope's EHR rows carry neither); the current
  // choice stays listed so a narrowed board can always say what narrowed it.
  const programIds = new Set(items.flatMap((i) => (i.programId ? [i.programId] : [])));
  const siteIds = new Set(items.flatMap((i) => (i.siteId ? [i.siteId] : [])));
  const programChoices = (programOptions.data?.programs ?? []).filter(
    (p) => programIds.has(p.id) || p.id === programFilter,
  );
  const siteChoices = (siteOptions.data?.sites ?? []).filter(
    (st) => siteIds.has(st.id) || st.id === siteFilter,
  );
  const workerChoices = [...new Set([...items.flatMap((i) => i.workerIds), workerFilter])]
    .filter(Boolean)
    .map((id) => ({ id, label: nameOf(id) }))
    .sort((a, b) => a.label.localeCompare(b.label));
  // Clients ONLY from the directory this reader may read, and only those on a
  // visit in the window — initials, as everywhere a roster names a client.
  const visitClientIds = new Set(items.flatMap((i) => (i.clientId ? [i.clientId] : [])));
  const clientChoices = (clients.data ?? []).filter(
    (c) => visitClientIds.has(c.id) || c.id === clientFilter,
  );
  const stateChoices = STATE_ORDER.filter(
    (st) => items.some((i) => i.state === st) || st === stateFilter,
  );

  /**
   * "No longer in Ritten": the EHR named by its connector when every imported
   * visit on screen came through one installation this reader can name, else
   * "the EHR". The record's own words, never a guess at a vendor.
   */
  const integrationIds = new Set(
    visitRows.flatMap((v) => (v.source === 'imported' && v.integrationId ? [v.integrationId] : [])),
  );
  const ehrName =
    integrationIds.size === 1
      ? (connectorLookup.get([...integrationIds][0]!)?.displayName ?? null)
      : null;
  const stateLabel = (st: CalendarState): string => calendarStateLabel(st, ehrName);

  const gridRows =
    isBoard && view !== 'month' ? boardRows(boardItems, cols, calendar.timeZone) : [];
  // The Unassigned row first — it is the one that needs a person — then by name.
  gridRows.sort((a, b) =>
    a.workerId === null
      ? -1
      : b.workerId === null
        ? 1
        : nameOf(a.workerId).localeCompare(nameOf(b.workerId)),
  );
  // "Show everyone": roster workers with nothing in the window, as empty rows
  // a scheduler can click into. Only offered when the roster itself is
  // readable — an empty roster read is not an empty organization.
  const shiftless =
    isBoard && view !== 'month' && showEveryone && workers.data
      ? workers.data
          .filter((w) => !gridRows.some((r) => r.workerId === w.id))
          .map((w) => ({
            workerId: w.id as string | null,
            cells: cols.map((): CalendarItem[] => []),
          }))
      : [];
  const allGridRows = [...gridRows, ...shiftless];
  const monthCells = view === 'month' ? tallyByDay(boardItems, cols, calendar.timeZone) : [];
  const drawerShift = drawerId ? (rows.find((s) => s.id === drawerId) ?? null) : null;
  const drawerVisit = visitDrawerId
    ? (items.find(
        (i): i is Extract<CalendarItem, { kind: 'visit' }> =>
          i.kind === 'visit' && i.id === visitDrawerId,
      ) ?? null)
    : null;
  const programNameOf = (id: string | null) =>
    id ? (programOptions.data?.programs.find((x) => x.id === id)?.name ?? null) : null;
  const siteNameOf = (id: string | null) =>
    id ? (siteOptions.data?.sites.find((x) => x.id === id)?.name ?? null) : null;

  /** Opens the drawer that item kind owns — one drawer at a time. */
  function openItem(item: CalendarItem): void {
    setDrawerId(item.kind === 'shift' ? item.id : null);
    setVisitDrawerId(item.kind === 'visit' ? item.id : null);
  }
  /** What a chip's tooltip says: time, whose visit, and its state in words. */
  function itemTitle(item: CalendarItem): string {
    const time =
      item.endsAt === item.startsAt
        ? clock(item.startsAt)
        : `${clock(item.startsAt)} – ${clock(item.endsAt)}`;
    const parts = [time];
    if (item.kind === 'visit') parts.push(`Visit · ${clientLabel(item.clientId)}`);
    if (item.kind === 'shift' && programNameOf(item.programId)) {
      parts.push(programNameOf(item.programId)!);
    }
    parts.push(stateLabel(item.state) + (item.detail ? ` (${item.detail})` : ''));
    if (item.unresolved) parts.push('needs a look');
    if (item.origin === 'ehr') parts.push(t('provenance.bookedInEhr'));
    return parts.join(' · ');
  }
  /** A chip's few characters: the time, and for a visit the client's initials. */
  function itemText(item: CalendarItem): string {
    if (item.kind === 'shift') return `${clock(item.startsAt)}–${clock(item.endsAt)}`;
    return `${clock(item.startsAt)} ${clientLabel(item.clientId)}`;
  }

  /** Click an empty cell → the create form, prefilled for that worker + day. */
  function prefillCreate(workerId: string, day: Date): void {
    if (!canWrite) return;
    const start = new Date(day);
    start.setHours(9, 0, 0, 0);
    const end = new Date(start.getTime() + 4 * 3_600_000);
    setCreateDraft({
      ...emptyShiftDraft(new Date()),
      workerId,
      startsAt: toDatetimeLocalInput(start.toISOString()),
      endsAt: toDatetimeLocalInput(end.toISOString()),
    });
    setCreating(true);
    setCreateError(null);
  }

  function stepAnchor(direction: -1 | 1): void {
    if (view === 'month') {
      setAnchor((a) => new Date(a.getFullYear(), a.getMonth() + direction, 1));
      return;
    }
    const step = view === 'day' ? 1 : 7;
    setAnchor((a) => new Date(a.getTime() + direction * step * DAY_MS));
  }

  const programPicker = pickerState(
    listPhase(programOptions, programOptions.data?.programs.length),
    'programs',
    'No program',
  );
  const sitePicker = pickerState(
    listPhase(siteOptions, siteOptions.data?.sites.length),
    'sites',
    'No fixed site',
  );
  const workerPicker = rosterWithheld
    ? // The query is disabled without `user:read` — or with one confined to a
      // program, which the route refuses the same way — and a disabled query is
      // not an empty organization.
      pickerState({ kind: 'refused' }, 'people', 'Choose a worker…')
    : canListWorkers
      ? pickerState(listPhase(workers, workers.data?.length), 'people', 'Choose a worker…')
      : // Not refused — not yet known. The set is loading or could not be read,
        // so the query has not fired and `listPhase` reads that as `loading`,
        // which is the honest word for it. Saying "refused" here would accuse
        // the reader of lacking a permission nobody has checked.
        pickerState({ kind: 'loading' }, 'people', 'Choose a worker…');

  function startEdit(s: SCH.Shift): void {
    setEditingId(s.id);
    setEditDraft(shiftDraftFromShift(s));
    setEditError(null);
  }

  const createErrors = validateShiftDraft(createDraft, { requireWorker: true });
  const canSubmitCreate = Object.keys(createErrors).length === 0 && createDraft.workerId !== '';
  const editErrors = validateShiftDraft(editDraft, { requireWorker: false });
  const canSubmitEdit = Object.keys(editErrors).length === 0;

  return (
    <div className="rn-col" style={{ maxWidth: 1080 }}>
      <SettingsHeader
        title="Scheduling"
        blurb="The organization’s calendar: worker shifts and client visits, and what happened to each."
      />

      {/*
        THE DOOR TO THE ASK QUEUE.

        Present only for a `schedule:manage` holder, which is the key the queue's
        own route charges: a link that leads to a refusal is worse than no link,
        because it reads as a permissions problem rather than as a screen that
        was never meant for this reader.
      */}
      {canManage ? (
        <p className="rn-muted" style={{ margin: 0 }}>
          A cancelled shift may carry a question nobody has answered.{' '}
          <button
            type="button"
            className="rn-link"
            style={{ padding: 0 }}
            onClick={() => navigate('/scheduling/cancellations')}
          >
            Unanswered cancellation asks →
          </button>
        </p>
      ) : null}

      <section className="rn-card sched-toolbar">
        <div className="sched-controls">
          <label className="cmp-filter">
            <span className="rn-muted">View</span>
            <select
              value={view}
              onChange={(e) => setView(e.target.value as View)}
              aria-label="Schedule view"
            >
              <option value="week">Week</option>
              <option value="day">Day</option>
              <option value="month">Month</option>
              <option value="coverage">Coverage gaps</option>
              <option value="all">All shifts</option>
            </select>
          </label>
          {isBoard ? (
            <>
              <div className="sched-nav" role="group" aria-label="Move the window">
                <button type="button" className="rn-btn-quiet" onClick={() => stepAnchor(-1)}>
                  ‹
                </button>
                <button
                  type="button"
                  className="rn-btn-quiet"
                  onClick={() => setAnchor(new Date())}
                >
                  Today
                </button>
                <button type="button" className="rn-btn-quiet" onClick={() => stepAnchor(1)}>
                  ›
                </button>
              </div>
              <span className="sched-range">
                {cols.length > 0
                  ? view === 'day'
                    ? formatDay(cols[0]!.toISOString())
                    : view === 'month'
                      ? anchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
                      : `${formatDay(cols[0]!.toISOString())} – ${formatDay(
                          cols[cols.length - 1]!.toISOString(),
                        )}`
                  : null}
              </span>
              {canListWorkers && view !== 'month' ? (
                <label className="cmp-filter sched-everyone">
                  <input
                    type="checkbox"
                    checked={showEveryone}
                    onChange={(e) => setShowEveryone(e.target.checked)}
                  />
                  <span className="rn-muted">Show everyone</span>
                </label>
              ) : null}
            </>
          ) : (
            <label className="cmp-filter">
              <span className="rn-muted">Window</span>
              <select
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                aria-label="Schedule window"
              >
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
              </select>
            </label>
          )}
          <span className="sched-stats rn-muted">
            {/* Each visit counted once — a group visit is one occurrence however
                many rows it sits in. Scheduled services exclude cancelled and
                no-longer-listed visits (owner ruling); those are said apart. */}
            {visitsRead
              ? [
                  `${scheduledServices} scheduled service${scheduledServices === 1 ? '' : 's'}`,
                  `${windowTally.delivered ?? 0} delivered`,
                  ...(['not_started', 'no_show', 'cancelled', 'unlisted', 'unclassified'] as const)
                    .filter((st) => (windowTally[st] ?? 0) > 0)
                    .map((st) => `${windowTally[st]} ${stateLabel(st)}`),
                ].join(' · ')
              : null}
            {visitsRead && periodShifts.length === 0 ? null : (
              <>
                {visitsRead ? ' · ' : ''}
                {ownShiftsOnly
                  ? `${periodShifts.length} of your shifts`
                  : `${periodShifts.length} shift${periodShifts.length === 1 ? '' : 's'}`}
                {` · ${periodUpcoming.length} upcoming`}
                {gaps.length > 0 ? (
                  <button
                    type="button"
                    className="rn-link"
                    onClick={() => setView('coverage')}
                  >{` · ${gaps.length} started with no session`}</button>
                ) : periodShifts.length > 0 ? (
                  ' · every started shift has a session'
                ) : null}
              </>
            )}
          </span>
        </div>
        {isBoard ? (
          <div className="sched-controls sched-filters">
            {workerChoices.length > 0 ? (
              <label className="cmp-filter">
                <span className="rn-muted">Worker</span>
                <select
                  value={workerFilter}
                  onChange={(e) => setWorkerFilter(e.target.value)}
                  aria-label="Filter by worker"
                >
                  <option value="">Everyone</option>
                  {workerChoices.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {clientChoices.length > 0 ? (
              <label className="cmp-filter">
                <span className="rn-muted">Client</span>
                <select
                  value={clientFilter}
                  onChange={(e) => setClientFilter(e.target.value)}
                  aria-label="Filter by client"
                >
                  <option value="">All clients</option>
                  {clientChoices.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.initials}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <label className="cmp-filter">
              <span className="rn-muted">Source</span>
              <select
                value={originFilter}
                onChange={(e) => setOriginFilter(e.target.value as '' | 'ehr' | 'phibian')}
                aria-label="Filter by source"
              >
                <option value="">EHR and Phibian</option>
                <option value="ehr">EHR</option>
                <option value="phibian">Phibian</option>
              </select>
            </label>
            {stateChoices.length > 0 ? (
              <label className="cmp-filter">
                <span className="rn-muted">Status</span>
                <select
                  value={stateFilter}
                  onChange={(e) =>
                    setStateFilter(e.target.value as '' | CalendarState | 'unresolved')
                  }
                  aria-label="Filter by status"
                >
                  <option value="">Any status</option>
                  {stateChoices.map((st) => (
                    <option key={st} value={st}>
                      {stateLabel(st)}
                    </option>
                  ))}
                  {items.some((i) => i.unresolved) || stateFilter === 'unresolved' ? (
                    <option value="unresolved">needs a look</option>
                  ) : null}
                </select>
              </label>
            ) : null}
            {programChoices.length > 0 ? (
              <label className="cmp-filter">
                <span className="rn-muted">Program</span>
                <select
                  value={programFilter}
                  onChange={(e) => setProgramFilter(e.target.value)}
                  aria-label="Filter by program"
                >
                  <option value="">All programs</option>
                  {programChoices.map((prg) => (
                    <option key={prg.id} value={prg.id}>
                      {prg.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {siteChoices.length > 0 ? (
              <label className="cmp-filter">
                <span className="rn-muted">Site</span>
                <select
                  value={siteFilter}
                  onChange={(e) => setSiteFilter(e.target.value)}
                  aria-label="Filter by site"
                >
                  <option value="">All sites</option>
                  {siteChoices.map((st) => (
                    <option key={st.id} value={st.id}>
                      {st.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {filtersActive ? (
              <button type="button" className="rn-btn-quiet" onClick={clearFilters}>
                {t('empty.filtered.clear')}
              </button>
            ) : null}
          </div>
        ) : null}
        {isBoard && siteFilter && visitsRead ? (
          <p className="rn-muted" role="note" style={{ margin: 'var(--space-6) 0 0' }}>
            {t('schedule.siteFilterShiftsOnly')}
          </p>
        ) : null}
      </section>

      {/* WHOSE VISITS, and whether there are any to show — four facts, never a zero. */}
      {visits.data?.scoped ? <ScopeNote scope={OWN_CASELOAD} /> : null}
      {visitsWithheld ? (
        <p className="rn-muted" role="note" style={{ margin: 0 }}>
          {t('schedule.visitsWithheld')}
        </p>
      ) : visitsFailed ? (
        <LoadFailure
          error={visits.error}
          message={t('schedule.visitsFailed')}
          onRetry={() => void visits.refetch()}
        />
      ) : null}

      {canWrite ? (
        <SettingsCard>
          <div className="rn-card-head">
            <h2 className="rn-card-title">Add a shift</h2>
            <button
              type="button"
              className="rn-btn-quiet"
              onClick={() => {
                setCreating((c) => !c);
                setCreateError(null);
              }}
              aria-expanded={creating}
            >
              {creating ? 'Cancel' : 'Create shift'}
            </button>
          </div>

          {creating ? (
            <ShiftForm
              draft={createDraft}
              errors={createErrors}
              onChange={setCreateDraft}
              workers={workers.data ?? []}
              programs={programOptions.data?.programs ?? []}
              sites={siteOptions.data?.sites ?? []}
              programPicker={programPicker}
              sitePicker={sitePicker}
              workerPicker={workerPicker}
              showWorker
              showStatus
              submitLabel={createShift.isPending ? 'Creating…' : 'Create shift'}
              canSubmit={canSubmitCreate}
              submitting={createShift.isPending}
              error={createError}
              onSubmit={() => createShift.mutate()}
              onCancel={() => {
                setCreating(false);
                setCreateDraft(emptyShiftDraft(new Date()));
                setCreateError(null);
              }}
            />
          ) : null}
        </SettingsCard>
      ) : null}

      {isBoard ? (
        <section className="rn-card sched-card">
          {view === 'month' ? null : allGridRows.length === 0 ? (
            filtersActive ? (
              <SuccessEmpty
                heading={t('empty.filtered')}
                message="Nothing in this window matches. Clear the filters to see the whole calendar."
              />
            ) : (
              <SuccessEmpty
                heading={
                  ownShiftsOnly
                    ? 'Nothing scheduled for you in this window'
                    : 'Nothing scheduled in this window'
                }
                message={`${t(visitsRead ? 'schedule.emptyWindow' : 'schedule.emptyShiftsOnly')} ${
                  canWrite
                    ? 'Add a shift, or step the window, to see the board fill in.'
                    : 'Step the window to another week, or check back once work is planned.'
                }`}
              />
            )
          ) : view === 'week' ? (
            <div className="sched-scroll">
              <div
                className="sched-grid"
                role="grid"
                aria-label="Week schedule, workers by day"
                style={{
                  gridTemplateColumns: `168px repeat(${cols.length}, minmax(118px, 1fr))`,
                }}
              >
                <div className="sched-corner" role="columnheader" aria-label="Worker" />
                {cols.map((d) => (
                  <div
                    key={d.toISOString()}
                    role="columnheader"
                    className="sched-dayhead"
                    data-today={dayKey(d) === orgToday ? 'true' : undefined}
                  >
                    {d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' })}
                  </div>
                ))}
                {allGridRows.map((r) => {
                  const workerId = r.workerId;
                  // Only a worker's empty day is a place to add a shift; the
                  // Unassigned row holds visits nobody is credited with.
                  const creatable = (cell: CalendarItem[]) =>
                    canWrite && workerId !== null && cell.length === 0;
                  return (
                    <div key={workerId ?? 'unassigned'} role="row" style={{ display: 'contents' }}>
                      <RowHead workerId={workerId} name={workerId ? nameOf(workerId) : null} />
                      {r.cells.map((cell, i) => (
                        <div
                          key={i}
                          role="gridcell"
                          className="sched-cell"
                          data-clickable={creatable(cell) ? 'true' : undefined}
                          tabIndex={creatable(cell) ? 0 : undefined}
                          aria-label={
                            creatable(cell) ? `Add a shift for ${nameOf(workerId!)}` : undefined
                          }
                          onClick={
                            creatable(cell) ? () => prefillCreate(workerId!, cols[i]!) : undefined
                          }
                          onKeyDown={
                            creatable(cell)
                              ? (e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    prefillCreate(workerId!, cols[i]!);
                                  }
                                }
                              : undefined
                          }
                        >
                          {cell.map((item) => (
                            <button
                              key={`${item.kind}:${item.id}`}
                              type="button"
                              className="sched-chip"
                              data-state={item.state}
                              data-kind={item.kind}
                              /* A chip is a few characters wide, so the canvas
                                 carries the ORIGIN as a marker and a tooltip and
                                 the drawer one click away carries the sentence.
                                 What it must never do is render an imported
                                 record as indistinguishable from a hand-entered
                                 one (PH-UX-002). */
                              data-source={
                                item.kind === 'shift' ? item.shift.source : item.visit.source
                              }
                              data-unresolved={item.unresolved ? 'true' : undefined}
                              title={itemTitle(item)}
                              onClick={(e) => {
                                e.stopPropagation();
                                openItem(item);
                              }}
                            >
                              <span className="sched-chip-time">{itemText(item)}</span>
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="sched-scroll">
              <div className="sched-daygrid" role="grid" aria-label="Day schedule, workers by hour">
                <div className="sched-dayaxis">
                  <span className="sched-corner" />
                  <div className="sched-hours">
                    {Array.from(
                      { length: DAY_AXIS.endHour - DAY_AXIS.startHour },
                      (_, i) => DAY_AXIS.startHour + i,
                    ).map((h) => (
                      <span key={h} className="sched-hour">
                        {((h + 11) % 12) + 1}
                        {h < 12 ? 'a' : 'p'}
                      </span>
                    ))}
                  </div>
                </div>
                {allGridRows.map((r) => {
                  const workerId = r.workerId;
                  const dayItems = r.cells[0] ?? [];
                  const creatable = canWrite && workerId !== null && dayItems.length === 0;
                  return (
                    <div key={workerId ?? 'unassigned'} className="sched-dayrow" role="row">
                      <RowHead workerId={workerId} name={workerId ? nameOf(workerId) : null} />
                      <div
                        className="sched-lane"
                        data-clickable={creatable ? 'true' : undefined}
                        tabIndex={creatable ? 0 : undefined}
                        aria-label={creatable ? `Add a shift for ${nameOf(workerId!)}` : undefined}
                        onClick={creatable ? () => prefillCreate(workerId!, cols[0]!) : undefined}
                        onKeyDown={
                          creatable
                            ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  prefillCreate(workerId!, cols[0]!);
                                }
                              }
                            : undefined
                        }
                      >
                        {dayItems.map((item) => {
                          const span = daySpan(item, cols[0]!, calendar.timeZone);
                          const total = DAY_AXIS.endHour - DAY_AXIS.startHour;
                          return (
                            <button
                              key={`${item.kind}:${item.id}`}
                              type="button"
                              className="sched-bar"
                              data-state={item.state}
                              data-kind={item.kind}
                              data-source={
                                item.kind === 'shift' ? item.shift.source : item.visit.source
                              }
                              data-unresolved={item.unresolved ? 'true' : undefined}
                              data-offaxis={span.offAxis ? 'true' : undefined}
                              style={{
                                left: `${((span.start - DAY_AXIS.startHour) / total) * 100}%`,
                                width: `${((span.end - span.start) / total) * 100}%`,
                              }}
                              title={
                                itemTitle(item) +
                                (span.offAxis ? ' · outside 6a–10p, pinned to the edge' : '')
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                openItem(item);
                              }}
                            >
                              {itemText(item)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {view === 'month' ? (
            <div className="sched-scroll">
              {/* COUNTS, NOT CHIPS: ~650 visits a month do not fit as chips. Each
                  item counts once on its start day; a day opens in the Day view. */}
              <div className="sched-month" role="grid" aria-label="Month schedule, counts by day">
                {cols.slice(0, 7).map((d) => (
                  <div key={d.toISOString()} role="columnheader" className="sched-dayhead">
                    {d.toLocaleDateString(undefined, { weekday: 'short' })}
                  </div>
                ))}
                {cols.map((d, i) => {
                  const cell = monthCells[i] ?? {};
                  return (
                    <button
                      key={d.toISOString()}
                      type="button"
                      role="gridcell"
                      className="sched-mday"
                      data-outside={d.getMonth() === anchor.getMonth() ? undefined : 'true'}
                      data-today={dayKey(d) === orgToday ? 'true' : undefined}
                      aria-label={`Open ${formatDay(d.toISOString())}`}
                      onClick={() => {
                        setAnchor(d);
                        setView('day');
                      }}
                    >
                      <span className="sched-mday-num">{d.getDate()}</span>
                      {STATE_ORDER.filter((st) => (cell[st] ?? 0) > 0).map((st) => (
                        <span key={st} className="sched-key" data-state={st}>
                          {cell[st]} {stateLabel(st)}
                        </span>
                      ))}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
          <p className="rn-muted sched-legend">
            {rows.length > 0 ? (
              <span className="sched-key" data-kind="shift">
                shift
              </span>
            ) : null}
            {visitItems.length > 0 ? (
              <span className="sched-key" data-kind="visit">
                client visit
              </span>
            ) : null}
            {STATE_ORDER.filter((st) => items.some((i) => i.state === st)).map((st) => (
              <span key={st} className="sched-key" data-state={st}>
                {stateLabel(st)}
              </span>
            ))}
            {items.some((i) => i.unresolved) ? (
              <span className="sched-key" data-unresolved="true">
                needs a look
              </span>
            ) : null}
          </p>
        </section>
      ) : null}

      {view !== 'all' ? (
        <section className="rn-card">
          <h2 className="rn-card-title">Shifts that started with no session</h2>
          <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
            A shift with no clock session may be a worker who forgot, a shift that was covered by
            someone else, or a plan that changed. Phibian records the gap; a person decides what it
            means.
          </p>
          {gaps.length === 0 ? (
            partialWindow ? (
              /*
               * NOT a success. Nothing was found in the rows that came back,
               * and there are more rows. `SuccessEmpty` would put a green
               * all-clear on a question that was only partly asked, which is
               * the same failure as reading an `own` list as the organization.
               */
              <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
                No shift in the first {shifts.data?.page.limit} of {shifts.data?.page.total} in this
                window started without a session — but this window holds more than one page, so this
                is not an all-clear for the window. Narrow the range to ask about all of it.
              </p>
            ) : visitMisses > 0 ? (
              /*
               * NOT a success either. The shifts are covered, but client visits
               * in the same window did not happen as planned — and for an EHR
               * tenant with no shifts at all, "every started shift has a
               * session" over an empty list was a green all-clear printed over
               * dozens of visits that never started.
               */
              <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
                No shift started without a session, but {visitMisses} client visit
                {visitMisses === 1 ? '' : 's'} in this window {visitMisses === 1 ? 'has' : 'have'}{' '}
                no recorded outcome — not started, no-show, still scheduled after the start time, or
                a status Phibian does not recognise. This is not an all-clear for planned work.
              </p>
            ) : (
              <SuccessEmpty
                heading={
                  ownShiftsOnly
                    ? 'Every started shift of yours has a session'
                    : 'Every started shift has a session'
                }
                message={
                  ownShiftsOnly
                    ? 'Nothing of yours has gone unrecorded in this window. This covers your own shifts only — managing the schedule is what widens it to the organization.'
                    : visitsRead
                      ? 'Nothing planned has gone unrecorded in this window.'
                      : 'No shift has gone unrecorded in this window. Client visits are not part of this check for you.'
                }
              />
            )
          ) : (
            <ul className="tc-rows">
              {gaps.map((shift) => (
                <li key={shift.id} className="tc-row">
                  <button
                    type="button"
                    className="tc-row-btn"
                    onClick={() => navigate(`/people/${shift.workerId}`)}
                  >
                    <span className="tc-row-main">
                      <span className="tc-row-name">{nameOf(shift.workerId)}</span>
                      <span className="rn-muted">
                        {formatDay(shift.startsAt)} · {formatClock(shift.startsAt)} –{' '}
                        {formatClock(shift.endsAt)}
                      </span>
                    </span>
                    <span className="tc-row-side">
                      <Pill variant="look" dot>
                        No session
                      </Pill>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {view === 'all' ? (
        <section className="rn-card">
          <h2 className="rn-card-title">All shifts in this window</h2>
          {rows.length === 0 ? (
            <SuccessEmpty
              heading={
                ownShiftsOnly
                  ? 'Nothing scheduled for you in this window'
                  : 'Nothing scheduled in this window'
              }
              message={
                ownShiftsOnly
                  ? `${t('schedule.shiftListOnly')} This window has no shifts assigned to you. Other people may be scheduled — seeing them needs permission to manage the schedule.`
                  : `${t('schedule.shiftListOnly')} Widen the window, or add shifts, to see them here.`
              }
            />
          ) : (
            <ul className="tc-rows">
              {[...rows]
                .sort((a, b) => a.startsAt.localeCompare(b.startsAt))
                .map((s) =>
                  editingId === s.id ? (
                    <li key={s.id} className="tc-row" style={{ padding: 'var(--space-12) 0' }}>
                      <ShiftForm
                        draft={editDraft}
                        errors={editErrors}
                        onChange={setEditDraft}
                        workers={workers.data ?? []}
                        programs={programOptions.data?.programs ?? []}
                        sites={siteOptions.data?.sites ?? []}
                        programPicker={programPicker}
                        sitePicker={sitePicker}
                        workerPicker={workerPicker}
                        showWorker={false}
                        showStatus={false}
                        fixedWorkerName={nameOf(s.workerId)}
                        submitLabel={updateShift.isPending ? 'Saving…' : 'Save changes'}
                        canSubmit={canSubmitEdit}
                        submitting={updateShift.isPending}
                        error={editError}
                        onSubmit={() => updateShift.mutate(s.id)}
                        onCancel={() => {
                          setEditingId(null);
                          setEditError(null);
                        }}
                      />
                    </li>
                  ) : (
                    <li
                      key={s.id}
                      className="tc-row"
                      style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)' }}
                    >
                      <button
                        type="button"
                        className="tc-row-btn"
                        style={{ flex: 1 }}
                        onClick={() => navigate(`/people/${s.workerId}`)}
                      >
                        <span className="tc-row-main">
                          <span className="tc-row-name">{nameOf(s.workerId)}</span>
                          <span className="rn-muted">
                            {formatDay(s.startsAt)} · {formatClock(s.startsAt)} –{' '}
                            {formatClock(s.endsAt)}
                            {/* PH-UX-002. A shift carries only `source` — no
                                installation id, no freshness, no vendor-absence
                                (unlike a planned VISIT; see Visits.tsx's
                                provenance.ts) — so an imported one can say only
                                THAT it was imported. `Origin` renders nothing
                                for `'manual'` or `'generated'`. */}
                            {s.source === 'imported' ? (
                              <>
                                {' · '}
                                <Origin source={s.source} />
                              </>
                            ) : null}
                          </span>
                        </span>
                        <span className="tc-row-side">
                          <Pill variant={shiftTone(s.status)} dot>
                            {s.status.replace(/_/g, ' ')}
                          </Pill>
                        </span>
                      </button>
                      {canWrite && s.status !== 'cancelled' ? (
                        <button
                          type="button"
                          className="rn-btn-quiet"
                          onClick={(e) => {
                            e.stopPropagation();
                            startEdit(s);
                          }}
                        >
                          Edit
                        </button>
                      ) : null}
                      {canManage && s.status !== 'cancelled' ? (
                        cancelingId === s.id ? (
                          <>
                            {/* ATTRIBUTION IS THE FIRST STEP, NOT AN AFTERTHOUGHT.
                                The confirm cannot be pressed until somebody has
                                said whose side this came from, because that
                                choice is what the record is for and because it
                                decides whether a question goes to a person. */}
                            <AttributionPicker
                              value={cancelAttribution}
                              onChange={setCancelAttribution}
                              id={`cancel-attribution-${s.id}`}
                            />
                            {/* Pending is keyed to THIS shift. `isPending`
                                alone described the mutation, not the row, so
                                one cancel in flight greyed out every other
                                row's confirm and said "Cancelling…" on none of
                                them in particular. */}
                            <button
                              type="button"
                              className="rn-btn-quiet"
                              disabled={
                                cancelAttribution === '' ||
                                (cancelShift.isPending && cancelShift.variables?.id === s.id)
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                if (cancelAttribution === '') return;
                                cancelShift.mutate({
                                  id: s.id,
                                  input: { attributedTo: cancelAttribution },
                                });
                              }}
                            >
                              {cancelShift.isPending && cancelShift.variables?.id === s.id
                                ? 'Cancelling…'
                                : cancelError?.id === s.id
                                  ? 'Try again'
                                  : 'Confirm cancel'}
                            </button>
                            <button
                              type="button"
                              className="rn-btn-quiet"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCancelingId(null);
                                setCancelError(null);
                                setCancelAttribution('');
                              }}
                            >
                              Keep it
                            </button>
                            {cancelError?.id === s.id ? (
                              <span
                                role="status"
                                className="rn-muted"
                                style={{ color: 'var(--color-urgent-deep)' }}
                              >
                                {cancelError.message} This shift is still on the schedule.
                              </span>
                            ) : null}
                          </>
                        ) : (
                          <button
                            type="button"
                            className="rn-btn-quiet"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCancelingId(s.id);
                              setCancelAttribution('');
                            }}
                          >
                            Cancel
                          </button>
                        )
                      ) : null}
                      {cancelOutcome?.id === s.id ? (
                        <span role="status" className="rn-muted">
                          {cancelOutcome.asked
                            ? 'Called off. The staff member has been asked once, with a deadline, for a reason.'
                            : 'Called off. Nobody was asked for a reason — this was not theirs to explain.'}{' '}
                          {cancelOutcome.rescheduleTracked
                            ? 'A task is tracking the replacement visit.'
                            : 'No replacement visit was required.'}
                        </span>
                      ) : null}
                    </li>
                  ),
                )}
            </ul>
          )}
        </section>
      ) : null}

      {/* The shift context drawer (Phase D): PLAN → ACTUAL → RECONCILED. When
          the drawer's shift is being edited, the drawer chrome holds the same
          ShiftForm the list uses — one form, two homes. */}
      {drawerVisit ? (
        <VisitDrawer
          item={drawerVisit}
          clientLabel={clientLabel(drawerVisit.clientId)}
          staffNames={drawerVisit.workerIds.map(nameOf)}
          stateLabel={stateLabel(drawerVisit.state)}
          connectorName={
            drawerVisit.visit.integrationId
              ? (connectorLookup.get(drawerVisit.visit.integrationId)?.displayName ?? null)
              : null
          }
          onClose={() => setVisitDrawerId(null)}
        />
      ) : null}

      {drawerShift ? (
        editingId === drawerShift.id ? (
          <aside className="drawer" aria-label={`Edit shift for ${nameOf(drawerShift.workerId)}`}>
            <div className="drawer-head">
              <h2 className="rn-card-title">Edit shift</h2>
              <button
                type="button"
                className="drawer-close"
                aria-label="Close"
                onClick={() => {
                  setEditingId(null);
                  setEditError(null);
                }}
              >
                ✕
              </button>
            </div>
            <ShiftForm
              draft={editDraft}
              errors={editErrors}
              onChange={setEditDraft}
              workers={workers.data ?? []}
              programs={programOptions.data?.programs ?? []}
              sites={siteOptions.data?.sites ?? []}
              programPicker={programPicker}
              sitePicker={sitePicker}
              workerPicker={workerPicker}
              showWorker={false}
              showStatus={false}
              fixedWorkerName={nameOf(drawerShift.workerId)}
              submitLabel={updateShift.isPending ? 'Saving…' : 'Save changes'}
              canSubmit={canSubmitEdit}
              submitting={updateShift.isPending}
              error={editError}
              onSubmit={() => updateShift.mutate(drawerShift.id)}
              onCancel={() => {
                setEditingId(null);
                setEditError(null);
              }}
            />
          </aside>
        ) : (
          <ShiftDrawer
            shift={drawerShift}
            workerName={nameOf(drawerShift.workerId)}
            workerUserId={userIdOf(drawerShift.workerId)}
            programName={programNameOf(drawerShift.programId)}
            siteName={siteNameOf(drawerShift.locationId)}
            now={now}
            onClose={() => {
              setDrawerId(null);
              setCancelingId(null);
              setCancelError(null);
            }}
            onEdit={(sh) => startEdit(sh)}
            onCancelShift={(sh) => {
              setCancelingId(sh.id);
              setCancelError(null);
              setCancelAttribution('');
            }}
            canWrite={canWrite}
            canManage={canManage}
            canceling={cancelingId === drawerShift.id}
            cancelPending={cancelShift.isPending && cancelShift.variables?.id === drawerShift.id}
            cancelErrorMessage={cancelError?.id === drawerShift.id ? cancelError.message : null}
            onConfirmCancel={(input) => cancelShift.mutate({ id: drawerShift.id, input })}
            onKeepShift={() => {
              setCancelingId(null);
              setCancelError(null);
              setCancelAttribution('');
            }}
          />
        )
      ) : null}
    </div>
  );
}

/** A board row's label: the worker (→ their schedule tab), or the Unassigned row. */
function RowHead({ workerId, name }: { workerId: string | null; name: string | null }) {
  const navigate = useNav();
  if (workerId === null) {
    return (
      <span
        className="sched-worker"
        data-unassigned="true"
        title="Visits no staff member here is credited with"
      >
        Unassigned
      </span>
    );
  }
  return (
    <button
      type="button"
      className="sched-worker"
      onClick={() => navigate(`/people/${workerId}?tab=schedule`)}
    >
      {name}
    </button>
  );
}

function shiftTone(status: string): 'good' | 'look' | 'unsure' | 'plain' {
  switch (status) {
    case 'completed':
      return 'good';
    case 'no_show':
      return 'look';
    case 'cancelled':
      return 'plain';
    case 'confirmed':
      return 'good';
    default:
      return 'unsure';
  }
}

// ---------------------------------------------------------------------------
// The create/edit form — one shape, used both ways.
// ---------------------------------------------------------------------------

interface ShiftFormProps {
  draft: ShiftDraft;
  errors: Record<string, string>;
  onChange: (draft: ShiftDraft) => void;
  workers: { id: string; displayName: string }[];
  programs: { id: string; name: string }[];
  sites: { id: string; name: string }[];
  /**
   * The four states of each picker, not just "is it loading".
   *
   * `programsLoading`/`sitesLoading` disabled the control while the read was in
   * flight and then RE-ENABLED it, empty, on failure — so a 403 or a 500 on
   * `GET /programs` produced a usable-looking dropdown offering only "No
   * program", and a scheduler concluded the organization had none. This is the
   * write-side twin of the read-side bug: an empty picker is an affirmative
   * statement, and the person forms an intention from it.
   */
  programPicker: PickerState;
  sitePicker: PickerState;
  workerPicker: PickerState;
  showWorker: boolean;
  showStatus: boolean;
  fixedWorkerName?: string;
  submitLabel: string;
  canSubmit: boolean;
  submitting: boolean;
  error: string | null;
  onSubmit: () => void;
  onCancel: () => void;
}

function ShiftForm({
  draft,
  errors,
  onChange,
  workers,
  programs: programOpts,
  sites: siteOpts,
  programPicker,
  sitePicker,
  workerPicker,
  showWorker,
  showStatus,
  fixedWorkerName,
  submitLabel,
  canSubmit,
  submitting,
  error,
  onSubmit,
  onCancel,
}: ShiftFormProps) {
  return (
    <form
      className="org-create"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) onSubmit();
      }}
    >
      {!showWorker && fixedWorkerName ? (
        <p className="rn-muted" style={{ fontSize: 13 }}>
          Editing {fixedWorkerName}’s shift. To assign this work to someone else, cancel it and
          create a new shift for them.
        </p>
      ) : null}
      <div className="org-create-grid">
        {showWorker ? (
          <SettingsField label="Worker" error={errors['workerId']}>
            {/* A shift cannot be created without a worker, so an empty roster
                read made the Create button permanently dead with nothing on
                screen explaining why — the reader saw a control that looked
                usable and was not. */}
            <select
              style={{ marginBottom: 0 }}
              value={draft.workerId}
              disabled={workerPicker.disabled}
              onChange={(e) => onChange({ ...draft, workerId: e.target.value })}
              autoFocus
            >
              <option value="">
                {workerPicker.disabled ? workerPicker.placeholder : 'Choose a worker…'}
              </option>
              {workers.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.displayName}
                </option>
              ))}
            </select>
            {workerPicker.note !== null ? (
              <p className="rn-muted" style={{ fontSize: 12 }} role="note">
                {workerPicker.note}
              </p>
            ) : null}
          </SettingsField>
        ) : null}
        {showStatus ? (
          <SettingsField label="Status">
            <select
              style={{ marginBottom: 0 }}
              value={draft.status}
              onChange={(e) =>
                onChange({ ...draft, status: e.target.value as ShiftDraft['status'] })
              }
            >
              <option value="scheduled">Scheduled</option>
              <option value="confirmed">Confirmed</option>
            </select>
          </SettingsField>
        ) : null}
      </div>

      <div className="org-create-grid">
        <SettingsField label="Starts" error={errors['startsAt']}>
          <input
            style={{ marginBottom: 0 }}
            type="datetime-local"
            value={draft.startsAt}
            onChange={(e) => onChange({ ...draft, startsAt: e.target.value })}
          />
        </SettingsField>
        <SettingsField label="Ends" error={errors['endsAt']}>
          <input
            style={{ marginBottom: 0 }}
            type="datetime-local"
            value={draft.endsAt}
            onChange={(e) => onChange({ ...draft, endsAt: e.target.value })}
          />
        </SettingsField>
      </div>

      <div className="org-create-grid">
        <SettingsField label="Program (optional)">
          <select
            style={{ marginBottom: 0 }}
            value={draft.programId}
            onChange={(e) => onChange({ ...draft, programId: e.target.value })}
            disabled={programPicker.disabled}
          >
            <option value="">
              {programPicker.disabled ? programPicker.placeholder : 'No program'}
            </option>
            {programOpts.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </SettingsField>
        <SettingsField label="Site (optional)">
          <select
            style={{ marginBottom: 0 }}
            value={draft.locationId}
            onChange={(e) => onChange({ ...draft, locationId: e.target.value })}
            disabled={sitePicker.disabled}
          >
            <option value="">
              {sitePicker.disabled ? sitePicker.placeholder : 'No fixed site'}
            </option>
            {siteOpts.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </SettingsField>
      </div>

      <SettingsField label="Notes (optional)" error={errors['notes']}>
        <textarea
          style={{ marginBottom: 0 }}
          rows={2}
          value={draft.notes}
          onChange={(e) => onChange({ ...draft, notes: e.target.value })}
        />
      </SettingsField>

      {error ? (
        <p style={{ color: 'var(--color-urgent-deep)', fontSize: 13 }} role="alert">
          {error}
        </p>
      ) : null}

      <div className="set-actions">
        <button
          type="submit"
          className="signin-devbtn"
          style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
          disabled={!canSubmit || submitting}
        >
          {submitLabel}
        </button>
        <button type="button" className="rn-btn-quiet" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
